import { Link } from "react-router-dom";
import { Github, Twitter, Linkedin } from "lucide-react";
import Container from "@/components/ui/Container";
import Logo from "@/components/common/Logo";

// Social profiles, each rendered only if it has a real URL. Fill these in (or
// wire them to config) as the accounts go live; an entry with a falsy href is
// skipped rather than rendered as a dead link.
const SOCIAL_LINKS = [
  { Icon: Github, label: "GitHub", href: "" },
  { Icon: Twitter, label: "X (Twitter)", href: "" },
  { Icon: Linkedin, label: "LinkedIn", href: "" },
];
const SOCIALS = SOCIAL_LINKS.filter((s) => s.href);

// Every entry here resolves to a real destination. This list used to carry ten
// `href="#"` placeholders (About, Blog, Careers, Docs, API, Status, Changelog and
// three social icons) that scrolled the page to the top and nothing else.
//
// The section links are absolute (`/#features`), not bare hashes: the footer also
// renders on /privacy and /terms, where those sections don't exist and a bare
// hash did nothing at all.
const COLS = [
  {
    title: "Product",
    links: [
      { label: "Features", to: "/#features" },
      { label: "How it works", to: "/#how" },
      { label: "Pricing", to: "/#pricing" },
      { label: "Extension", to: "/extension" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Contact", action: "contact" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="border-t border-slate-200/70 bg-white dark:border-white/10 dark:bg-ink-950">
      <Container className="py-14">
        <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-[2fr_1fr_1fr]">
          <div>
            <Logo />
            <p className="mt-4 max-w-xs text-sm text-slate-500 dark:text-slate-400">
              Describe a test in plain English. AI plans it, runs it in the cloud,
              finds the bug, and writes the report.
            </p>
            {/* Rendered only when a real profile URL is configured. Three icons
                all pointing at "#" and all announcing themselves as "social link"
                were three dead links and one repeated label to a screen reader. */}
            {SOCIALS.length > 0 && (
              <div className="mt-5 flex gap-2">
                {SOCIALS.map(({ Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${label} (opens in a new tab)`}
                    title={label}
                    className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:text-brand-600 dark:border-white/10 dark:text-slate-400 dark:hover:text-brand-300"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                {col.title}
              </h4>
              <ul className="mt-4 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.action === "contact" ? (
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent("aiqa:contact"))}
                        className="text-sm text-slate-500 transition-colors hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-300"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        to={link.to}
                        className="text-sm text-slate-500 transition-colors hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-300"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-slate-200/70 pt-6 text-sm text-slate-400 dark:border-white/10 sm:flex-row">
          <p>© {new Date().getFullYear()} AI-QA-Engine. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-brand-500">Privacy</Link>
            <Link to="/terms" className="hover:text-brand-500">Terms</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
}
